/**
 * Unsubscribe hooks — used by journey-web's public `/unsubscribe` page.
 *
 * Unauthenticated: the recipient arrives from an email footer link and may not
 * have an account at all. The token in the URL is the credential.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { NotificationBucket } from '@tonnie/types';
import { useSdkContext } from '../providers/SdkProvider';
import { createUnsubscribeService } from '../services/unsubscribe.service';

export const unsubscribeKeys = {
  all: ['unsubscribe'] as const,
  preview: (token: string, bucket: string) =>
    [...unsubscribeKeys.all, 'preview', token, bucket] as const,
};

/**
 * Read what is about to be unsubscribed. Deliberately a QUERY and not a
 * mutation: it changes nothing, which is what lets the page load safely when a
 * mail scanner or link previewer fetches the URL unattended.
 */
export function usePreviewUnsubscribe(
  token: string | null | undefined,
  bucket: NotificationBucket | null | undefined
) {
  const { client } = useSdkContext();
  const service = createUnsubscribeService(client);

  return useQuery({
    queryKey: unsubscribeKeys.preview(token ?? '', bucket ?? ''),
    queryFn: () => {
      if (!token || !bucket) throw new Error('Missing unsubscribe token');
      return service.preview(token, bucket);
    },
    enabled: !!token && !!bucket,
    // No retry: a 400/404 here means the link is malformed or revoked, and
    // retrying a bad link three times only delays telling the recipient.
    retry: false,
    staleTime: Infinity,
  });
}

/** Perform the opt-out. Idempotent, so a double-click is harmless. */
export function useConfirmUnsubscribe() {
  const { client } = useSdkContext();
  const service = createUnsubscribeService(client);

  return useMutation({
    mutationFn: ({ token, bucket }: { token: string; bucket: NotificationBucket }) =>
      service.confirm(token, bucket),
  });
}
